import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { useStaffAuth } from '@/features/auth/useStaffAuth';
import { formatInstant } from '@/features/inventory/registerUi';
import {
  useCommandClearances,
  type CommandClearance,
} from '@/features/clearance/useClearance';
import { WithdrawClearanceDialog } from '@/features/clearance/WithdrawClearanceDialog';
import { ClearDevicePickerDialog } from '@/features/clearance/ClearDevicePickerDialog';

/**
 * SLICE 14c — COMMAND CLEARANCES. Every device commandable ONLY because a named
 * person said so: cleared, and NOT hardened.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS SCREEN EXISTS TO BE LOOKED AT.
 * ─────────────────────────────────────────────────────────────────────────────
 * A clearance is a temporary, accepted-risk state by intent, and the way a
 * temporary state becomes permanent is that nothing ever enumerates it. So it
 * gets its own nav entry rather than living only as an inline cell on the bench:
 * the whole point is that it is not buried. Each row should eventually be
 * replaced by hardening; this list is how anyone can tell whether that is
 * happening.
 *
 * SUPER-ADMIN ONLY, gated here and not only in the nav — `GET
 * /admin/command-clearances` carries IMEIs and is `requireSuperAdmin`, so a deep
 * link by an `admin_staff` would render a screen whose every fetch 403s. Same
 * shape as the commissioning bench.
 *
 * NO ARTBOARD EXISTS FOR THIS SCREEN — the command console (`5f`'s nav entry) was
 * never drawn as a screen. The furniture matches the shipped admin lists (Tenants,
 * the registers) rather than an approximated pixel source, stated rather than
 * implied.
 */
export function Clearances() {
  const { t, i18n } = useTranslation();
  const { state } = useStaffAuth();
  const isSuperAdmin = state.status === 'authenticated' && state.user.role === 'super_admin';
  const q = useCommandClearances(isSuperAdmin);
  const [withdrawing, setWithdrawing] = useState<CommandClearance | null>(null);
  const [picking, setPicking] = useState(false);

  if (state.status === 'authenticated' && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  if (q.isPending) {
    return (
      <section aria-labelledby="clearances-heading">
        <h1 id="clearances-heading" className="font-display text-xl font-bold tracking-tight">
          {t('clearance.list.title')}
        </h1>
        <p className="mt-4 text-sm text-raad-muted" role="status">
          {t('clearance.list.loading')}
        </p>
      </section>
    );
  }

  const rows = Array.isArray(q.data?.clearances) ? q.data.clearances : null;

  if (q.isError || rows === null) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('clearance.list.error')}
      </p>
    );
  }

  return (
    <section aria-labelledby="clearances-heading">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h1 id="clearances-heading" className="font-display text-xl font-bold tracking-tight">
          {t('clearance.list.title')}
        </h1>
        {/* Slice 14d. The verb the screen was missing: the action that MAKES a
            clearance, next to the list that enumerates them. Always visible so
            the empty state has somewhere to point. Super-admin only reaches this
            screen (the redirect above), so no extra gate is needed here. */}
        <button
          type="button"
          onClick={() => setPicking(true)}
          className="order-last ms-auto min-h-9 rounded-lg bg-raad-primary px-3 text-[12.5px] font-semibold text-raad-onprimary"
        >
          {t('clearance.pick.action')}
        </button>
        <p className="text-sm text-raad-muted">
          {t('clearance.list.summary', { count: rows.length })}
        </p>
      </div>
      <p className="mt-1 max-w-2xl text-[12.5px] text-raad-muted">
        {t('clearance.list.explainer')}
      </p>

      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{t('clearance.list.empty')}</p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-panel border border-raad-border bg-raad-panel px-4">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('clearance.list.col.imei')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('clearance.list.col.protocol')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('clearance.list.col.clearedAt')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('clearance.list.col.note')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('clearance.list.col.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.deviceId} className="border-b border-raad-border last:border-b-0">
                  <td className="py-3 pr-4 align-top">
                    <span className="num font-medium">{c.imei}</span>
                  </td>
                  <td className="py-3 pr-4 align-top">
                    <span className="num">{c.protocol ?? '—'}</span>
                  </td>
                  <td className="py-3 pr-4 align-top">
                    {/* Warn tone, because this is an accepted risk, not a healthy
                        state — the same colour the bench cell uses for it. */}
                    <span className="num text-raad-warn">
                      {c.commandEnabledAt !== null
                        ? formatInstant(c.commandEnabledAt, i18n.language)
                        : '—'}
                    </span>
                  </td>
                  <td className="py-3 pr-4 align-top">
                    <span className="text-[12.5px]">
                      {c.commandEnabledNote ?? (
                        <span className="text-raad-muted">—</span>
                      )}
                    </span>
                  </td>
                  <td className="py-3 pr-4 align-top">
                    <button
                      type="button"
                      onClick={() => setWithdrawing(c)}
                      className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold transition-colors hover:bg-raad-primary/10"
                    >
                      {t('clearance.withdraw.action')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {withdrawing !== null ? (
        <WithdrawClearanceDialog
          device={{ deviceId: withdrawing.deviceId, imei: withdrawing.imei }}
          onClose={() => setWithdrawing(null)}
        />
      ) : null}

      {picking ? <ClearDevicePickerDialog onClose={() => setPicking(false)} /> : null}
    </section>
  );
}

export default Clearances;
